import type { SizeData } from '@design-system/components/Pdp/PdpFitFinder.props'

export const useSizeSelector = (
  algoliaSizes: ComputedRef<{ sku: string; size: string; disabled: boolean }[]>,
  isExtSizes: boolean = false,
  sizesData: SizeData[] = []
) => {
  const { selectedCountry, sizeCountries } = useSizeCountry()

  const filteredSortedAlgolia = computed(() => {
    if (!sizesData?.length) return algoliaSizes.value
    const order = new Map<string, number>(
      sizesData.map((s, i) => [s.TGL_COD!, i])
    )
    return algoliaSizes.value
      .filter(s => order.has(s.size))
      .sort((a, b) => order.get(a.size)! - order.get(b.size)!)
  })

  const sizes = computed(() => {
    const generateLocalizedSizes = (country: string) => {
      return filteredSortedAlgolia.value.map(algoliaSize => {
        const sizeData = sizesData?.find(sd => sd.TGL_COD === algoliaSize.size)
        const localizedSize = sizeData
          ? (sizeData[`${country}_ALS`] ?? sizeData['TGL_COD'])
          : algoliaSize.size
        return {
          ...algoliaSize,
          size: localizedSize as string,
          sizeCode: sizeData ? sizeData['TGL_COD'] : algoliaSize.size,
        }
      })
    }

    return Object.fromEntries(
      sizeCountries.map(country => [
        country,
        sizesData?.length > 0
          ? generateLocalizedSizes(country)
          : algoliaSizes.value,
      ])
    )
  })

  // preselect onesize se disponibile sul primo elemento (dopo filtro/ordinamento)
  const getInitialSizeIndex = () => {
    const list =
      sizesData?.length > 0 ? filteredSortedAlgolia.value : algoliaSizes.value
    const firstSize = list?.[0]
    return !firstSize?.disabled &&
      (firstSize?.size === 'Onesize' || firstSize?.size === 'TU')
      ? 0
      : null
  }

  const selectedSizeIndex = ref<number | null>(getInitialSizeIndex())

  const selectedSize = computed(() => {
    if (isExtSizes) selectedSizeIndex.value = 0
    return selectedSizeIndex.value !== null
      ? sizes.value[selectedCountry.value][selectedSizeIndex.value]
      : null
  })

  return {
    sizes,
    selectedCountry,
    selectedSizeIndex,
    selectedSize,
  }
}
